import React, { useState } from 'react';
import API_URL from './config';
import './Login.css';

function Login({ onLogin, onRegister, onHome }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  
  // Email verification state
  const [showVerification, setShowVerification] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationError, setVerificationError] = useState('');
  const [verificationSuccess, setVerificationSuccess] = useState('');
  const [verificationLoading, setVerificationLoading] = useState(false);
  
  // Contact Us state
  const [showContact, setShowContact] = useState(false);
  const [contactFirstName, setContactFirstName] = useState('');
  const [contactLastName, setContactLastName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactOrg, setContactOrg] = useState('');
  const [contactMessage, setContactMessage] = useState('');
  const [contactSending, setContactSending] = useState(false);
  const [contactSuccess, setContactSuccess] = useState('');
  const [contactError, setContactError] = useState('');
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMessage, setForgotMessage] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  
  // Password change state
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [tempToken, setTempToken] = useState(null);
  const [tempUser, setTempUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changeError, setChangeError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });
      const data = await res.json();
      if (!res.ok) {
        // Check if email verification is required
        if (data.requiresVerification && data.email) {
          setVerificationEmail(data.email);
          setShowVerification(true);
          return;
        }
        throw new Error(data.error || 'Login failed');
      }
      
      // Check if user must change password
      if (data.mustChangePassword) {
        setTempToken(data.token);
        setTempUser(data.user);
        setMustChangePassword(true);
        setPassword('');
      } else {
        onLogin(data);
      }
    } catch (err) {
      setError(err.message);
    }
  };

  // Handle email verification
  const handleVerifyEmail = async (e) => {
    e.preventDefault();
    setVerificationError('');
    setVerificationSuccess('');
    setVerificationLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/verify-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: verificationEmail, code: verificationCode })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Verification failed');
      
      setVerificationSuccess('Email verified! You can now log in.');
      setTimeout(() => {
        setShowVerification(false);
        setVerificationCode('');
        setVerificationEmail('');
        setVerificationSuccess('');
      }, 2000);
    } catch (err) {
      setVerificationError(err.message);
    }
    setVerificationLoading(false);
  };
  
  // Resend verification code
  const handleResendVerification = async () => {
    setVerificationError('');
    setVerificationSuccess('');
    setVerificationLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: verificationEmail })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to resend code');
      
      setVerificationSuccess('A new verification code has been sent to your email.');
    } catch (err) {
      setVerificationError(err.message);
    }
    setVerificationLoading(false);
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotMessage('');
    setForgotLoading(true);
    
    try {
      const res = await fetch(`${API_URL}/api/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to process request');
      
      setForgotMessage(data.message);
      setForgotEmail('');
    } catch (err) {
      setForgotError(err.message);
    } finally {
      setForgotLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setChangeError('');
    
    if (newPassword.length < 6) {
      setChangeError('Password must be at least 6 characters');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setChangeError('Passwords do not match');
      return;
    }
    
    try {
      const res = await fetch(`${API_URL}/api/change-password`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${tempToken}`
        },
        body: JSON.stringify({ newPassword })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to change password');
      
      // Password changed successfully, complete login
      onLogin({ token: tempToken, user: tempUser });
    } catch (err) {
      setChangeError(err.message);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    if (!contactEmail || !contactMessage) {
      setContactError('Please enter your email and message.');
      return;
    }
    setContactSending(true);
    setContactError('');
    setContactSuccess('');
    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: contactFirstName,
          lastName: contactLastName,
          organization: contactOrg,
          email: contactEmail,
          message: contactMessage
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send message');
      setContactSuccess('Message sent successfully! We will get back to you soon.');
      setContactMessage('');
    } catch (err) {
      setContactError(err.message);
    }
    setContactSending(false);
  };

  const resetContactForm = () => {
    setShowContact(false);
    setContactFirstName('');
    setContactLastName('');
    setContactEmail('');
    setContactOrg('');
    setContactMessage('');
    setContactSuccess('');
    setContactError('');
  };

  // Password change form
  if (mustChangePassword) {
    return (
      <div className="login-container">
        <form onSubmit={handleChangePassword}>
          <h2>Change Password</h2>
          <p className="change-password-info">
            You must change your temporary password before continuing.
          </p>
          {changeError && <div className="error">{changeError}</div>}
          <input
            type="password"
            placeholder="New Password"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            required
            minLength={6}
          />
          <input
            type="password"
            placeholder="Confirm New Password"
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            required
            minLength={6}
          />
          <button type="submit">Change Password</button>
          <button 
            type="button" 
            className="cancel-btn"
            onClick={() => {
              setMustChangePassword(false);
              setTempToken(null);
              setTempUser(null);
              setNewPassword('');
              setConfirmPassword('');
            }}
          >
            Cancel
          </button>
        </form>
      </div>
    );
  }

  // Forgot password form
  if (showForgotPassword) {
    return (
      <div className="login-container">
        <form onSubmit={handleForgotPassword}>
          <h2>Forgot Password</h2>
          <p className="forgot-password-info">
            Enter your email address and we'll send you a temporary password.
          </p>
          {forgotError && <div className="error">{forgotError}</div>}
          {forgotMessage && <div className="success">{forgotMessage}</div>}
          <input
            type="email"
            placeholder="Email Address"
            value={forgotEmail}
            onChange={e => setForgotEmail(e.target.value)}
            required
          />
          <button type="submit" disabled={forgotLoading}>
            {forgotLoading ? 'Sending...' : 'Send Temporary Password'}
          </button>
          <button 
            type="button" 
            className="cancel-btn"
            onClick={() => {
              setShowForgotPassword(false);
              setForgotEmail('');
              setForgotError('');
              setForgotMessage('');
            }}
          >
            Back to Login
          </button>
        </form>
      </div>
    );
  }

  // Show email verification screen
  if (showVerification) {
    return (
      <div className="login-container">
        <form onSubmit={handleVerifyEmail}>
          <h2>Verify Your Email</h2>
          <p className="verification-info">
            We've sent a 6-digit verification code to <strong>{verificationEmail}</strong>
          </p>
          {verificationError && <div className="error">{verificationError}</div>}
          {verificationSuccess && <div className="success">{verificationSuccess}</div>}
          <input
            type="text"
            placeholder="Enter 6-digit code"
            value={verificationCode}
            onChange={e => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            required
            maxLength={6}
            style={{ textAlign: 'center', letterSpacing: '8px', fontSize: '24px' }}
          />
          <button type="submit" disabled={verificationLoading || verificationCode.length !== 6}>
            {verificationLoading ? 'Verifying...' : 'Verify Email'}
          </button>
          <button 
            type="button" 
            className="link-btn"
            onClick={handleResendVerification}
            disabled={verificationLoading}
          >
            Resend Code
          </button>
          <button 
            type="button" 
            className="link-btn"
            onClick={() => {
              setShowVerification(false);
              setVerificationCode('');
              setVerificationEmail('');
              setVerificationError('');
              setVerificationSuccess('');
            }}
          >
            Back to Login
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="login-container">
      <form onSubmit={handleSubmit}>
        <h2>Login</h2>
        {error && <div className="error">{error}</div>}
        <input
          type="text"
          placeholder="Username"
          value={username}
          onChange={e => setUsername(e.target.value)}
          required
        />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={e => setPassword(e.target.value)}
          required
        />
        <button type="submit">Login</button>
        <button 
          type="button" 
          className="link-btn"
          onClick={onRegister}
        >
          Need an account? Register
        </button>
        <button 
          type="button" 
          className="forgot-password-btn"
          onClick={() => setShowForgotPassword(true)}
        >
          Forgot Password?
        </button>
        <button 
          type="button" 
          className="forgot-password-btn"
          onClick={() => setShowContact(true)}
        >
          Contact Us
        </button>
        {onHome && (
          <button 
            type="button" 
            className="forgot-password-btn"
            onClick={onHome}
          >
            Return to Home
          </button>
        )}
      </form>
      
      {showContact && (
        <div className="contact-modal-overlay" onClick={resetContactForm}>
          <div className="contact-modal" onClick={e => e.stopPropagation()}>
            <h2>Contact Us</h2>
            <p className="contact-subtitle">Questions about registration or booking? We're here to help!</p>
            
            {contactSuccess && <div className="success">{contactSuccess}</div>}
            {contactError && <div className="error">{contactError}</div>}
            
            <form onSubmit={handleContactSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>First Name</label>
                  <input 
                    type="text" 
                    value={contactFirstName} 
                    onChange={e => setContactFirstName(e.target.value)}
                    placeholder="Your first name"
                  />
                </div>
                <div className="form-group">
                  <label>Last Name</label>
                  <input 
                    type="text" 
                    value={contactLastName} 
                    onChange={e => setContactLastName(e.target.value)}
                    placeholder="Your last name"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Organization / Sport</label>
                <input 
                  type="text" 
                  value={contactOrg} 
                  onChange={e => setContactOrg(e.target.value)}
                  placeholder="e.g., Youth Soccer League, Baseball Team"
                />
              </div>
              
              <div className="form-group">
                <label>Email *</label>
                <input 
                  type="email" 
                  value={contactEmail} 
                  onChange={e => setContactEmail(e.target.value)} 
                  required 
                  placeholder="Your email address for our reply"
                />
              </div>
              
              <div className="form-group">
                <label>Message *</label>
                <textarea 
                  value={contactMessage} 
                  onChange={e => setContactMessage(e.target.value)} 
                  required 
                  rows="5" 
                  placeholder="How can we help you?"
                />
              </div>
              
              <div className="modal-actions">
                {contactSuccess ? (
                  <button type="button" className="submit-btn" onClick={resetContactForm}>Close</button>
                ) : (
                  <>
                    <button type="button" className="cancel-btn" onClick={resetContactForm}>Cancel</button>
                    <button type="submit" className="submit-btn" disabled={contactSending}>
                      {contactSending ? 'Sending...' : 'Send Message'}
                    </button>
                  </>
                )}
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Login;
